import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import { isTrackCodeInternalUser } from "common/trackcodeInternal";
import { fetchArtifacts, fetchWorkspace } from "./CollaborationService";
import {
  resolveHeroArtifact,
  resolveHistoryArtifacts
} from "./collaborationUtils";
import JourneyStepper from "./components/JourneyStepper";
import HeroCard from "./components/HeroCard";
import ArtifactList from "./components/ArtifactList";
import CollaborationHeader, {
  navigateCollaborationAfterTenantSwitch
} from "./components/CollaborationHeader";
import CollaborationContentLoading from "./components/CollaborationContentLoading";
import {
  COLLABORATION_INDEX_PATH,
  didCollaborationTenantContextChange,
  shouldFetchCollaborationData
} from "./collaborationTenantChange";

class CollaborationWorkspace extends Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        slug: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
      replace: PropTypes.func.isRequired
    }).isRequired,
    user: PropTypes.object,
    logisticCompanyId: PropTypes.number,
    logisticCompanyPrepareChange: PropTypes.bool
  };

  state = {
    workspace: null,
    artifacts: [],
    loading: true,
    error: null
  };

  componentDidMount() {
    if (shouldFetchCollaborationData(this.props)) {
      this.loadWorkspace();
    }
  }

  componentDidUpdate(prevProps) {
    if (didCollaborationTenantContextChange(prevProps, this.props)) {
      if (shouldFetchCollaborationData(this.props)) {
        this.props.history.replace(COLLABORATION_INDEX_PATH);
      }
      return;
    }

    const prevSlug = prevProps.match.params.slug;
    const nextSlug = this.props.match.params.slug;

    if (prevSlug !== nextSlug) {
      this.loadWorkspace();
    }
  }

  loadWorkspace = async () => {
    const { slug } = this.props.match.params;
    this.setState({ loading: true, error: null, workspace: null, artifacts: [] });

    try {
      const [workspace, artifacts] = await Promise.all([
        fetchWorkspace(slug),
        fetchArtifacts(slug)
      ]);
      this.setState({ workspace, artifacts, loading: false });
    } catch (error) {
      const status = error?.response?.status;
      this.setState({
        loading: false,
        error:
          status === 404
            ? "Workspace nicht gefunden."
            : "Workspace konnte nicht geladen werden."
      });
    }
  };

  handleArtifactClick = artifact => {
    const href = artifact?.nextStep?.href || artifact?.href;
    if (typeof href === "string" && href.startsWith("/")) {
      this.props.history.push(href);
      return;
    }
    const { slug } = this.props.match.params;
    this.props.history.push(`/collaboration/${slug}/artifacts/${artifact.key}`);
  };

  render() {
    const { user } = this.props;
    const { workspace, artifacts, loading, error } = this.state;
    const isInternalUser = isTrackCodeInternalUser(user);
    const visibleArtifacts = isInternalUser
      ? artifacts
      : artifacts.filter(
          artifact =>
            artifact.visibility === "shared" || !artifact.visibility
        );
    const heroArtifact = workspace
      ? resolveHeroArtifact(visibleArtifacts, { isInternalUser })
      : null;
    const historyArtifacts = workspace
      ? resolveHistoryArtifacts(visibleArtifacts, heroArtifact)
      : [];

    return (
      <Page>
        <CollaborationHeader
          partner={workspace?.partner}
          onAfterSwitch={navigateCollaborationAfterTenantSwitch}
        />

        {workspace?.journey && <JourneyStepper journey={workspace.journey} />}

        <ContentBody>
          {loading ? (
            <CollaborationContentLoading />
          ) : error || !workspace ? (
            <EmptyState>
              <EmptyTitle>{error || "Workspace nicht gefunden."}</EmptyTitle>
            </EmptyState>
          ) : (
            <>
              <SectionLabel>Aktuell</SectionLabel>
              {heroArtifact ? (
                <HeroCard
                  artifact={heroArtifact}
                  onClick={() => this.handleArtifactClick(heroArtifact)}
                />
              ) : (
                <EmptyHero>
                  Noch kein Artefakt für diesen Workspace verfügbar.
                </EmptyHero>
              )}

              <SectionLabel $spaced>Verlauf</SectionLabel>
              <ArtifactList
                artifacts={historyArtifacts}
                isInternalUser={isInternalUser}
                onArtifactClick={this.handleArtifactClick}
              />
            </>
          )}
        </ContentBody>
      </Page>
    );
  }
}

const mapStateToProps = state => ({
  user: state.auth?.user,
  logisticCompanyId: state.auth?.logisticCompanyId,
  logisticCompanyPrepareChange: state.auth?.logisticCompanyPrepareChange
});

export default withRouter(connect(mapStateToProps)(CollaborationWorkspace));

const Page = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f7f6f2;
  overflow: auto;
`;

const ContentBody = styled.div`
  flex: 1;
  padding: 24px 32px 32px;
  overflow: auto;
`;

const SectionLabel = styled.h2`
  margin: ${({ $spaced }) => ($spaced ? "40px 0 12px" : "0 0 12px")};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a09e96;
`;

const EmptyHero = styled.div`
  padding: 24px;
  background: #fff;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  color: #a09e96;
  font-size: 13px;
`;

const EmptyState = styled.section`
  max-width: 520px;
  margin: 48px auto 0;
  text-align: center;
`;

const EmptyTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #181714;
`;
